<template>
  <div class="detail-root">
    <!-- 顶栏：返回 + 账号 -->
    <div class="top-bar">
      <span class="back" @click="goBack">↩</span>
      <div class="account-badge">
        <span class="avatar">{{ userName.charAt(0) }}</span>
        <div class="name-line">
          <span class="name">{{ userName }}</span>
          <span class="nick" v-if="nickName">{{ nickName }}</span>
        </div>
      </div>
      <div class="top-actions">
        <span class="range-btn" :class="{ on: range === 'today' }" @click="setRange('today')">今</span>
        <span class="range-btn" :class="{ on: range === 'yesterday' }" @click="setRange('yesterday')">昨</span>
        <span class="range-btn" :class="{ on: range === '2d' }" @click="setRange('2d')">2d</span>
        <span class="range-btn" :class="{ on: range === '3d' }" @click="setRange('3d')">3d</span>
        <span class="range-btn" :class="{ on: range === '7d' }" @click="setRange('7d')">7d</span>
      </div>
    </div>

    <!-- 实时状态 -->
    <div class="status-strip">
      <div class="ss-item">
        <span class="ss-label">状态</span>
        <span class="ss-val" :class="online ? 'on' : 'off'">{{ online ? '直播中' : '离线' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">所在房间</span>
        <span class="ss-val">{{ resolveRoomName(room) || '未进房' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">上线时间</span>
        <span class="ss-val">{{ onlineTime ? fmtTime(onlineTime) : '—' }}</span>
      </div>
      <div class="ss-item">
        <span class="ss-label">最后离线</span>
        <span class="ss-val">{{ lastOfflineTime ? fmtTime(lastOfflineTime) : '—' }}</span>
      </div>
    </div>

    <!-- 直播统计 -->
    <div class="stats-bar" v-if="debugMode">
      <div class="stat-card">
        <span class="sc-val">{{ totalCount }}</span>
        <span class="sc-label">直播场次</span>
      </div>
      <div class="stat-card" :class="netWinCardClass">
        <span class="sc-val" :class="netWinClass">{{ totalNetWinStr }}</span>
        <span class="sc-label">直播收入</span>
      </div>
    </div>

    <!-- 内容区：撑满剩余空间，内部滚动 -->
    <div class="content-wrap">
      <div class="scroll-area">
        <div v-if="loading" class="loading">正在拉取直播数据...</div>
        <div v-else-if="records.length === 0" class="empty">暂无直播记录</div>

        <div class="record" v-for="r in records" :key="r.order_number || r.id" :class="[recordBoxClass(r), { unfinished: !r.is_finish }]">
          <!-- 头部：直播间信息 -->
          <div class="rec-top">
            <div class="rec-top-row1">
              <span class="live-dot" v-if="!r.is_finish"></span>
              <span class="rec-desk">{{ roomDisplayName(r) }}</span>
              <span class="rec-status" :class="{ done: r.is_finish }">
                {{ r.is_finish ? '已结束' : '直播中' }}
              </span>
              <span class="rec-result-chip" v-if="debugMode && betLabel(r)" :class="betColorClass(r)">
                {{ betLabel(r) }}<i v-if="r.odds"> · {{ r.odds }}</i>
              </span>
            </div>
          </div>

          <!-- PK 对战区 - 真实模式：传统扑克牌 -->
          <div class="pk-real-showdown" v-if="debugMode && (r.xian_cards || r.zhuang_cards)">
            <!-- 信息行 -->
            <div class="showdown-info">
              <span class="rec-time">{{ formatTime(r.bet_time) }}</span>
              <span class="rec-round">第{{ r.main_ju_number }}回合</span>
              <span class="rec-settle-time" v-if="r.settlement_time">结束于 {{ formatTime(r.settlement_time) }}</span>
            </div>
            <!-- 对决行 -->
            <div class="showdown-row">
              <!-- 我方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.xian_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
              <!-- VS 比分 -->
              <div class="showdown-vs">
                <span class="vs-score-xian" v-if="r.xian_dian != null">{{ r.xian_dian }}</span>
                <span class="vs-word">vs</span>
                <span class="vs-score-zhuang" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}</span>
              </div>
              <!-- 对方卡牌 -->
              <div class="playing-card" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci" :class="c.suit">
                <span class="pc-rank">{{ realRankDisplay(c.count) }}</span>
                <span class="pc-suit">{{ realSuitSymbol(c.suit) }}</span>
              </div>
              <div v-if="!parseCards(r.zhuang_cards).length" class="playing-card empty">
                <span class="pc-rank">-</span>
              </div>
            </div>
          </div>

          <!-- PK 对战区 - 礼物模式：图标徽章 -->
          <div class="pk-zone" v-else-if="!debugMode && (r.xian_cards || r.zhuang_cards)">
            <div class="pk-row">
              <span class="pk-tag blue">我方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.xian_cards)" :key="'x'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.xian_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.xian_dian != null">{{ r.xian_dian }}<i>分</i></span>
            </div>
            <div class="pk-divider"><span>VS</span></div>
            <div class="pk-row">
              <span class="pk-tag red">对方</span>
              <span class="pk-cards">
                <span class="chip" :class="c.suit" v-for="(c, ci) in parseCards(r.zhuang_cards)" :key="'z'+ci">
                  {{ c.symbol }}×{{ c.count }}
                </span>
                <span v-if="!parseCards(r.zhuang_cards).length" class="chip empty">-</span>
              </span>
              <span class="pk-pts" v-if="r.zhuang_dian != null">{{ r.zhuang_dian }}<i>分</i></span>
            </div>
          </div>

          <!-- 收益明细 -->
          <div class="rec-amounts">
            <div class="am">
              <span class="am-icon">投</span>
              <span class="am-val gold">{{ Tools.formatMoney(r.total_betting_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">赢</span>
              <span class="am-val" :class="winColor(r.real_win_total_amount)">{{ Tools.formatMoney(r.real_win_total_amount) }}</span>
            </div>
            <div class="am">
              <span class="am-icon">额</span>
              <span class="am-val dim">{{ Tools.formatMoney(r.front_balance) }} → {{ Tools.formatMoney(r.after_balance) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部分页：始终在底部 -->
      <div class="pager-bar">
        <span class="pg-btn" :class="{ off: currentPage <= 1 }" @click="goPage(currentPage - 1)">‹</span>
        <span class="pg-num" v-if="totalPages > 0">{{ currentPage }} / {{ totalPages }} 页</span>
        <span class="pg-num" v-else>暂无数据</span>
        <span class="pg-btn" :class="{ off: currentPage >= totalPages }" @click="goPage(currentPage + 1)">›</span>
      </div>
    </div>

    <!-- 悬浮刷新 -->
    <span class="float-refresh" @click="loadData()">↻</span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, inject, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Tools from '@/ts/Tools';
import { GAME_TYPE_MAP } from '@/ts/roomName';
import { resolveRoomName, setRoomNameMap } from '@/ts/roomNameStore';
import RoomVO from '@/ts/VO/RoomVO';
import SignalRClient from '@/ts/SignalRClient';

const route = useRoute();
const router = useRouter();
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));
const userName = ref(route.params.userName as string || '');
const nickName = ref('');

// 返回时回到主播列表页（首页的“主播列表”标签）
function goBack() {
  router.push({ name: 'home', query: { tab: 'recharge' } });
}

// 实时状态（来自列表接口 / GetAccountBets / 服务端推送）
// 进入详情时先用列表已知的实时状态做种子，避免接口滞后导致误判“离线”
const online = ref(route.query.online === '1');
const onlineTime = ref((route.query.ot as string) || '');
const lastOfflineTime = ref((route.query.lo as string) || '');
const room = ref((route.query.room as string) || '');

const range = ref('today');
const loading = ref(false);
const records = ref<any[]>([]);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);
const totalNetWin = ref(0);
const pageSize = 20;

// 推送已处理过的订单号 / 该订单已计入的收入金额（用于去重，重新拉页时清空）
const pushedOrders = new Set<string>();
const pushedWin = new Map<string, number>();

// ===== 伪装/真实卡牌切换（由父组件统一 debugMode 控制） =====
const REAL_SUIT: Record<string, string> = { spade: '♠', heart: '♥', club: '♣', diamond: '♦' };
const REAL_RANK: Record<number, string> = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
function realSuitSymbol(suit: string) { return REAL_SUIT[suit] || ''; }
function realRankDisplay(n: number) { return REAL_RANK[n] || String(n); }

const totalNetWinStr = computed(() => {
  const v = totalNetWin.value;
  return Tools.formatMoney(v, { showSign: true });
});
const netWinClass = computed(() => totalNetWin.value > 0 ? 'positive' : totalNetWin.value < 0 ? 'negative' : '');
const netWinCardClass = computed(() => totalNetWin.value > 0 ? 'card-win' : totalNetWin.value < 0 ? 'card-lose' : '');
function setRange(r: string) {
  range.value = r;
  currentPage.value = 1;
  loadData();
}

function goPage(p: number) {
  if (p < 1 || p > totalPages.value) return;
  currentPage.value = p;
  loadData();
}

async function loadData() {
  loading.value = true;
  try {
    const now = new Date();
    let start: string, end: string = toDateStr(now);
    if (range.value === 'today') start = end;
    else if (range.value === 'yesterday') { start = end = toDateStr(new Date(now.getTime() - 86400000)); }
    else if (range.value === '2d') start = toDateStr(new Date(now.getTime() - 86400000));
    else if (range.value === '3d') start = toDateStr(new Date(now.getTime() - 2 * 86400000));
    else if (range.value === '7d') start = toDateStr(new Date(now.getTime() - 6 * 86400000));
    else start = toDateStr(new Date(now.getTime() - 29 * 86400000));

    const resp = await Tools.FetchGetAsync<any>('game/account-bets', {
      userName: userName.value,
      startDate: start,
      endDate: end,
      page: currentPage.value,
      pageSize: pageSize
    });
    if (resp.CheckIsOk() && resp.Data) {
      records.value = resp.Data.data || [];
      // 整页重拉：服务端数据才是准的，清空推送去重账本
      pushedOrders.clear();
      pushedWin.clear();
      totalPages.value = resp.Data.pagecount || 0;
      totalCount.value = resp.Data.allcount || 0;
      // 后端聚合的统计，不再前端拉全量数据计算
      if (resp.Data.stats) {
        totalNetWin.value = resp.Data.stats.total_win || 0;
      }
      if (records.value.length > 0 && records.value[0].nickName) {
        nickName.value = records.value[0].nickName;
      }
      // 实时状态字段（服务端实时计算）
      // online 以“已确认为在线”为准：列表种子或服务端推送若已判在线，接口的滞后 false 不将其降级
      online.value = online.value || !!resp.Data.online;
      onlineTime.value = resp.Data.onlineTime || onlineTime.value;
      lastOfflineTime.value = resp.Data.lastOfflineTime || lastOfflineTime.value;
      room.value = resp.Data.room || room.value;
    }
  } finally {
    loading.value = false;
  }
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ===== 直播间互动玩法映射（见 @/ts/roomName）=====
function roomDisplayName(r: any): string {
  const zhuo = r.game_zhuo_name || '';
  const gn = r.game_name || '';
  const gc = zhuo; //r.game_code || '';

  // if (gn && zhuo) return `${gn} ${zhuo}`;
  // if (gn) return `${gn} ${gc}`;
  // if (zhuo) return zhuo;
  // 兜底：用前端映射
  const type = typeof r.game_type === 'string' ? parseInt(r.game_type) : r.game_type;
  const name = GAME_TYPE_MAP[type];
  if (name && gc) return `${name} ${gc}`;
  return name || gc || '';
}

// ===== 直播互动结果判断（仅 debug/真实模式显示） =====

function betLabel(r: any): string {
  if (!r.is_finish) return '';
  return r.bet_direction || '';
}

function betColorClass(r: any): string {
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'result-win';
  if (win < 0) return 'result-lose';
  return '';
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function winColor(val: number): string {
  if (val > 0) return 'positive';
  if (val < 0) return 'negative';
  return 'zero';
}

function recordBoxClass(r: any): string {
  const win = Number(r.real_win_total_amount) || 0;
  if (win > 0) return 'record-win';
  if (win < 0) return 'record-lose';
  return '';
}

// ===== 卡牌解析 =====
const SUIT_MAP: Record<string, { symbol: string; cls: string }> = {
  S: { symbol: '🖤', cls: 'spade' },    // 黑桃 — 黑色心/桃
  H: { symbol: '❤️', cls: 'heart' },    // 红桃 — 红色心
  C: { symbol: '🌸', cls: 'club' },     // 梅花 — 花
  D: { symbol: '🟦', cls: 'diamond' },  // 方片 — 方块
};
const RANK_VALUE: Record<string, number> = { '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, '11': 11, '12': 12, '13': 13 };

function parseCards(raw: string | null): { count: number; symbol: string; suit: string }[] {
  if (!raw || raw === '-' || raw === '[]') return [];
  try {
    const arr: string[] = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((c: string) => typeof c === 'string' && c.includes('.'))
      .map((c: string) => {
        const [suit, rank] = c.split('.');
        const info = SUIT_MAP[suit] || { symbol: suit, cls: '' };
        return { count: RANK_VALUE[rank] || 0, symbol: info.symbol, suit: info.cls };
      });
  } catch {
    return [];
  }
}

// 下注/结算推送：服务端已带完整记录，前端直接插入/更新列表，不重新拉取整页
function applyPushedRecord(rec: any) {
  // 只处理当前查看的账号；列表按 bet_time 倒序，新记录只可能落在第 1 页
  if (!rec || !rec.order_number) return;
  if (rec.userName !== userName.value) return;
  if (currentPage.value !== 1) return;
  // “昨天”区间不含今天，推送的新记录不属于该区间，直接丢弃
  if (range.value === 'yesterday') return;

  const no = rec.order_number;
  const idx = records.value.findIndex((r: any) => r && r.order_number === no);
  if (idx >= 0) {
    // 已存在（例如刚打开页面已经刷到这条）→ 原地更新，不新增条目，避免重复
    records.value[idx] = rec;
  } else if (!pushedOrders.has(no)) {
    // 新记录插到最前；超过每页固定条数则移除末尾多余的（推送 100 条也只显示一页）
    records.value = [rec, ...records.value].slice(0, pageSize);
    totalCount.value += 1;                                   // 直播场次
    totalPages.value = Math.max(totalPages.value, Math.ceil(totalCount.value / pageSize));
  }
  pushedOrders.add(no);

  // 直播收入：仅已结算才计入；同一单只累加差额，避免下注→结算两次推送重复计
  if (rec.is_finish === 1) {
    const win = Number(rec.real_win_total_amount) || 0;
    totalNetWin.value += win - (pushedWin.get(no) || 0);
    pushedWin.set(no, win);
  }
  if (rec.nickName) nickName.value = rec.nickName;
}
const onAccountDataPush = (data: any) => applyPushedRecord(data?.record);

onMounted(() => {
  loadData();
  // 兜底：深链/刷新进入时首页未加载房间列表，主动拉一次填充 RoomNumber->RoomName 映射
  SignalRClient.Instance.InvokeAsync<{ [key: string]: RoomVO }>("GetRoomList")
    .then((resp) => {
      const list: RoomVO[] = [];
      for (const key in resp) {
        const room = resp[key];
        Object.setPrototypeOf(room, RoomVO.prototype);
        list.push(room);
      }
      setRoomNameMap(list);
    })
    .catch(() => {});
  // 服务端推送的账号状态实时更新（在线/房间/时间）
  SignalRClient.Instance.AddEventListener("onUserStatusUpdate", (data: any) => {
    if (data?.userName !== userName.value) return;
    online.value = !!data.online;
    onlineTime.value = data.onlineTime || '';
    lastOfflineTime.value = data.lastOfflineTime || '';
    room.value = data.room || '';
  });
  // 下注/结算推送：服务端带完整记录，前端直接插入/更新（去重 + 限制每页条数）
  SignalRClient.Instance.AddEventListener("onBet", onAccountDataPush);
  SignalRClient.Instance.AddEventListener("onSettle", onAccountDataPush);
});

onUnmounted(() => {
  SignalRClient.Instance.RemoveEventListener("onUserStatusUpdate");
  SignalRClient.Instance.RemoveEventListener("onBet");
  SignalRClient.Instance.RemoveEventListener("onSettle");
});

function fmtTime(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
</script>

<style scoped lang="scss">
.detail-root {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// ===== 顶栏 =====
.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.back {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  font-size: 15px;
  color: var(--kp-gold);
  font-weight: 700;
  background: rgba(216, 178, 95, 0.08);
  border: 1px solid rgba(216, 178, 95, 0.20);
  transition: all .15s;
  user-select: none;

  &:hover {
    background: rgba(216, 178, 95, 0.16);
    border-color: rgba(240, 208, 138, 0.45);
    box-shadow: 0 0 18px -8px rgba(240, 208, 138, 0.7);
    color: var(--kp-gold-hi);
  }
}

.account-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fbeecb 0%, #e8c98a 42%, #cfa559 100%);
  color: #241a09;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow:
    0 0 0 1px rgba(255, 240, 205, 0.45),
    0 0 22px -8px rgba(240, 208, 138, 0.65);
}

.name-line {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: .6px;
  color: var(--kp-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nick {
  font-size: 11px;
  color: var(--kp-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-actions {
  display: flex;
  gap: 2px;
  background: rgba(255, 240, 210, 0.035);
  border: 1px solid rgba(216, 178, 95, 0.12);
  border-radius: 9px;
  padding: 3px;
  flex-shrink: 0;
}

.range-btn {
  font-size: 11px;
  color: var(--kp-muted);
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover { color: var(--kp-text-2); }

  &.on {
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.26), rgba(168, 124, 57, 0.10));
    color: var(--kp-gold-hi);
    font-weight: 700;
    box-shadow:
      0 0 14px -7px rgba(240, 208, 138, 0.65),
      inset 0 1px 0 rgba(255, 242, 208, 0.14);
  }
}

// ===== 实时状态条 =====
.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  background: rgba(255, 240, 210, 0.015);
  flex-shrink: 0;

  .ss-item {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }

  .ss-label {
    font-size: 10px;
    color: var(--kp-faint);
    letter-spacing: .8px;
    flex-shrink: 0;
  }

  .ss-val {
    font-size: 12px;
    font-weight: 600;
    color: var(--kp-text-2);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &.on { color: var(--kp-jade); }
    &.off { color: var(--kp-muted); }
  }
}

// ===== 统计条 =====
.stats-bar {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.stat-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: linear-gradient(160deg, rgba(255, 240, 210, 0.05), rgba(255, 240, 210, 0.015));
  border-radius: 10px;
  padding: 10px 6px;
  border: 1px solid rgba(216, 178, 95, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
  transition: all 0.3s;

  &.card-win {
    background: linear-gradient(160deg, rgba(126, 201, 164, 0.12), rgba(126, 201, 164, 0.03));
    border-color: rgba(126, 201, 164, 0.22);
    .sc-label { color: var(--kp-jade); }
  }

  &.card-lose {
    background: linear-gradient(160deg, rgba(205, 106, 83, 0.12), rgba(205, 106, 83, 0.03));
    border-color: rgba(205, 106, 83, 0.22);
    .sc-label { color: var(--kp-bad-soft); }
  }
}

.sc-val {
  font-size: 20px;
  font-weight: 800;
  color: var(--kp-gold-hi);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-family: var(--kp-mono);
  text-shadow: 0 0 16px rgba(216, 178, 95, 0.30);

  &.positive { color: var(--kp-jade); text-shadow: 0 0 16px rgba(126, 201, 164, 0.30); }
  &.negative { color: var(--kp-bad-soft); text-shadow: 0 0 16px rgba(205, 106, 83, 0.30); }
}

.sc-label {
  font-size: 10px;
  color: var(--kp-muted);
  font-weight: 500;
  letter-spacing: .8px;
}

// ===== 内容区（列表 + 分页）=====
.content-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 10px;
  min-height: 0;
  min-width: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.20);
    border-radius: 2px;
  }
}

.loading, .empty {
  text-align: center;
  padding: 80px 0;
  color: var(--kp-faint);
  font-size: 13px;
  letter-spacing: 1px;
}

// ===== 记录卡片 =====
.record {
  background: linear-gradient(160deg, rgba(255, 240, 210, 0.045), rgba(255, 240, 210, 0.012));
  border: 1px solid rgba(216, 178, 95, 0.12);
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
  padding: 10px 12px;
  margin-bottom: 10px;
  transition: all .2s;
  overflow: hidden;
  min-width: 0;

  &:hover {
    background: linear-gradient(160deg, rgba(216, 178, 95, 0.09), rgba(216, 178, 95, 0.025));
    border-color: rgba(216, 178, 95, 0.24);
  }

  &.unfinished {
    border-color: rgba(216, 178, 95, 0.28);
  }

  &.record-win {
    background: linear-gradient(160deg, rgba(126, 201, 164, 0.09), rgba(126, 201, 164, 0.02));
    border-color: rgba(126, 201, 164, 0.18);
    &:hover { background: linear-gradient(160deg, rgba(126, 201, 164, 0.13), rgba(126, 201, 164, 0.04)); }
  }

  &.record-lose {
    background: linear-gradient(160deg, rgba(205, 106, 83, 0.09), rgba(205, 106, 83, 0.02));
    border-color: rgba(205, 106, 83, 0.18);
    &:hover { background: linear-gradient(160deg, rgba(205, 106, 83, 0.13), rgba(205, 106, 83, 0.04)); }
  }
}

// ===== 卡片头部 =====
.rec-top {
  display: flex;
  margin-bottom: 10px;
}

.rec-top-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
}

// 脉冲金点 - 直播中
.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(180deg, #fdf0cd, #d8b25f);
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(240, 208, 138, 0.7);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}

.rec-desk {
  font-size: 13px;
  color: var(--kp-text);
  font-weight: 700;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rec-status {
  font-size: 10px;
  color: var(--kp-jade);
  background: rgba(126, 201, 164, 0.12);
  border: 1px solid rgba(126, 201, 164, 0.20);
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;

  &.done {
    color: var(--kp-muted);
    background: rgba(255, 240, 210, 0.05);
    border-color: rgba(216, 178, 95, 0.12);
  }
}

.rec-time {
  font-size: 10px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.rec-round {
  font-size: 10px;
  color: var(--kp-gold);
  font-weight: 600;
  white-space: nowrap;
}

.rec-settle-time {
  font-size: 9px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.rec-result-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 6px;
  margin-left: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  background: rgba(255, 240, 210, 0.05);
  color: var(--kp-muted);
  border: 1px solid rgba(216, 178, 95, 0.12);

  i {
    font-style: normal;
    color: var(--kp-gold);
    font-weight: 500;
    margin-left: 2px;
  }

  &.result-win {
    color: var(--kp-jade);
    background: rgba(126, 201, 164, 0.08);
    border-color: rgba(126, 201, 164, 0.16);
  }

  &.result-lose {
    color: var(--kp-bad-soft);
    background: rgba(205, 106, 83, 0.08);
    border-color: rgba(205, 106, 83, 0.16);
  }
}

// ===== PK 对战区 =====
.pk-zone {
  position: relative;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.02);
  border: 1px solid rgba(216, 178, 95, 0.10);
}

.pk-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  flex-wrap: wrap;
}

.pk-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  flex-shrink: 0;
  min-width: 36px;
  text-align: center;

  &.blue {
    color: var(--kp-gold-hi);
    background: rgba(216, 178, 95, 0.12);
    border: 1px solid rgba(216, 178, 95, 0.24);
  }
  &.red {
    color: var(--kp-bad-soft);
    background: rgba(205, 106, 83, 0.12);
    border: 1px solid rgba(205, 106, 83, 0.24);
  }
}

.pk-cards {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  min-width: 0;
}

.pk-pts {
  margin-left: auto;
  font-size: 16px;
  font-weight: 900;
  color: var(--kp-gold-hi);
  letter-spacing: 1px;
  flex-shrink: 0;
  text-shadow: 0 0 14px rgba(216, 178, 95, 0.35);

  i {
    font-style: normal;
    font-size: 10px;
    color: var(--kp-faint);
    font-weight: 500;
    margin-left: 2px;
  }
}

.pk-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  margin: 3px 0 3px 36px;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: calc(50% - 24px);
    background: rgba(216, 178, 95, 0.10);
  }
  &::before { left: 0; }
  &::after  { right: 0; }

  span {
    font-size: 10px;
    font-weight: 800;
    color: var(--kp-faint);
    letter-spacing: 1px;
    padding: 0 8px;
    z-index: 1;
  }
}


// ===== 礼物标记 =====
.chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  line-height: 1.2;
  letter-spacing: 0.3px;
  border: 1px solid transparent;

  &.spade   { color: #e2b87a; border-color: rgba(226,184,122,0.22); background: rgba(226,184,122,0.08); }
  &.club    { color: #a9e3c6; border-color: rgba(169,227,198,0.20); background: rgba(169,227,198,0.06); }
  &.heart   { color: #f0a094; border-color: rgba(240,160,148,0.22); background: rgba(240,160,148,0.08); }
  &.diamond { color: #a9c6cf; border-color: rgba(169,198,207,0.20); background: rgba(169,198,207,0.06); }

  &.empty {
    color: var(--kp-faint);
    font-weight: 400;
    background: transparent;
    border-color: transparent;
  }
}

// ===== 真实扑克牌对决布局 =====
.pk-real-showdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.025);
  border: 1px solid rgba(216, 178, 95, 0.12);
}

.showdown-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.showdown-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

// 对决中心
.showdown-vs {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 8px;
  flex-shrink: 0;

  .vs-score-xian {
    font-size: 22px;
    font-weight: 900;
    color: var(--kp-gold-hi);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-shadow: 0 0 16px rgba(216, 178, 95, 0.40);
  }
  .vs-score-zhuang {
    font-size: 22px;
    font-weight: 900;
    color: var(--kp-bad-soft);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-shadow: 0 0 16px rgba(205, 106, 83, 0.35);
  }
  .vs-word {
    font-size: 10px;
    font-weight: 700;
    color: rgba(216, 178, 95, 0.30);
    letter-spacing: 0.5px;
    text-transform: lowercase;
    flex-shrink: 0;
  }
}

// ===== 传统扑克牌 =====
.playing-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 42px;
  border-radius: 4px;
  background: linear-gradient(145deg, #f7f2e6, #e9e2d0);
  border: 1px solid rgba(216,178,95,0.45);
  box-shadow:
    0 1px 3px rgba(0,0,0,0.35),
    0 0 12px -6px rgba(216,178,95,0.55),
    inset 0 0 0 1px rgba(255,255,255,0.28);
  flex-shrink: 0;

  .pc-rank {
    font-size: 14px;
    font-weight: 900;
    line-height: 1;
  }

  .pc-suit {
    font-size: 14px;
    line-height: 1;
    margin-top: -1px;
  }

  &.spade, &.club {
    .pc-rank, .pc-suit { color: #1a1a2e; }
  }
  &.heart, &.diamond {
    .pc-rank, .pc-suit { color: #c53030; }
  }

  &.empty {
    background: rgba(255,240,210,0.02);
    border: 1px dashed rgba(216,178,95,0.18);
    box-shadow: none;
    .pc-rank { color: var(--kp-faint); font-weight: 400; }
  }
}

// ===== 收益明细 =====
.rec-amounts {
  display: flex;
  justify-content: space-around;
  min-width: 0;
}

.am {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.am-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: var(--kp-gold);
  background: rgba(216, 178, 95, 0.10);
  border: 1px solid rgba(216, 178, 95, 0.18);
  border-radius: 3px;
  flex-shrink: 0;
}

.am-val {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-family: var(--kp-mono);

  &.gold    { color: var(--kp-gold-hi); text-shadow: 0 0 12px rgba(216, 178, 95, 0.30); }
  &.positive { color: var(--kp-jade); }
  &.negative { color: var(--kp-bad-soft); }
  &.zero    { color: var(--kp-muted); }
  &.dim     { color: var(--kp-muted); font-size: 10px; font-weight: 400; }
}

// ===== 底部分页：固定 =====
.pager-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 10px 16px;
  border-top: 1px solid var(--kp-gold-line);
  flex-shrink: 0;
}

.pg-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  font-size: 16px;
  color: var(--kp-gold);
  cursor: pointer;
  user-select: none;
  transition: all .15s;

  &:hover {
    background: rgba(216, 178, 95, 0.12);
    color: var(--kp-gold-hi);
  }

  &.off {
    opacity: 0.25;
    pointer-events: none;
  }
}

.pg-num {
  font-size: 12px;
  color: var(--kp-muted);
  font-variant-numeric: tabular-nums;
  letter-spacing: .5px;
  min-width: 60px;
  text-align: center;
}

// ===== 悬浮刷新 =====
.float-refresh {
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  color: var(--kp-gold);
  background: linear-gradient(180deg, rgba(216, 178, 95, 0.22), rgba(168, 124, 57, 0.10));
  border: 1px solid rgba(216, 178, 95, 0.34);
  cursor: pointer;
  user-select: none;
  transition: all .15s;
  z-index: 10;
  backdrop-filter: blur(6px);
  box-shadow:
    0 6px 20px -10px #000,
    0 0 22px -12px rgba(240, 208, 138, 0.65);

  &:hover {
    color: #fff6e0;
    background: linear-gradient(180deg, rgba(216, 178, 95, 0.34), rgba(168, 124, 57, 0.16));
    border-color: rgba(246, 227, 182, 0.60);
    box-shadow:
      0 6px 24px -10px #000,
      0 0 28px -10px rgba(240, 208, 138, 0.85);
  }

  &:active {
    transform: scale(0.9) rotate(180deg);
  }
}
</style>
